import { useEffect, useState, type ReactNode } from "react";
import { BRIEFING_LABELS, briefingCaptionTags, type EffectiveBriefing } from "@talentkontor/domain";
import { toggleTheme } from "../../lib/theme";
import { Toasts } from "../../components/Toast";
import { WEBSITE_URL } from "../../app/nav";
import { CreatorOfficeLogo } from "../../components/shell/Logo";
import { PLATFORM_NAME } from "../../lib/marke";
import { useInvoiceLogo } from "../../model/invoice-document";
import { fmtDate, textParts, type Lang } from "./marke-model";
import "./marke.css";

/**
 * Bausteine der öffentlichen Seiten für Marken (0073): Rahmen mit Sprache und Hell/Dunkel, Briefing, Text mit Links.
 * Auch im Agentur-Bereich als Vorschau eingebettet (ohne Rahmen). Der Rahmen trägt klein das CreatorOffice-Logo; die Agentur
 * steht mit ihrem Logo im Inhalt (AgenturKopf) und mit ihrem Kontakt im Fuß.
 */

/** Dokumentkopf der Agentur im Inhalt der Seite: ihr Logo (Bucket branding), sonst ihr Name. */
export function AgenturKopf({ agency }: { agency: { name: string; logo_path?: string | null } }) {
  const logo = useInvoiceLogo(agency.logo_path ?? null);
  return (
    <div className="tk-marke-agentur">
      {logo?.url ? <img className="tk-marke-agentur-logo" src={logo.url} alt={agency.name} /> : <strong>{agency.name}</strong>}
    </div>
  );
}

/** Sprache der Marke als Vorgabe, umschaltbar; das Dokument trägt die Sprache (lang) mit. */
export function useMarkenSprache(initial: Lang | null): [Lang, (l: Lang) => void] {
  const browser: Lang = typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("en") ? "en" : "de";
  const [own, setOwn] = useState<Lang | null>(null);
  const lang = own ?? initial ?? browser;
  useEffect(() => {
    const before = document.documentElement.lang;
    document.documentElement.lang = lang;
    return () => { document.documentElement.lang = before; };
  }, [lang]);
  return [lang, setOwn];
}

export function MarkenRahmen({ lang, onLang, agency, children }: {
  lang: Lang; onLang: (l: Lang) => void; agency: { name: string; address: string | null; email: string | null; website: string | null } | null; children: ReactNode;
}) {
  const other: Lang = lang === "de" ? "en" : "de";
  return (
    <>
      {/* Persönlicher Link: kein Suchindex; der Schlüssel in der Adresse wandert nie als Referrer mit */}
      <meta name="robots" content="noindex, nofollow, noarchive" />
      <meta name="referrer" content="no-referrer" />
      <a className="skip btn" href="#main">{lang === "en" ? "Skip to content" : "Zum Inhalt"}</a>
      <header className="webnav tk-noprint">
        <span className="brand co-brand"><CreatorOfficeLogo form="responsive" size="sm" /></span>
        <div className="row">
          <button type="button" lang={other} onClick={() => onLang(other)}>{other === "en" ? "English" : "Deutsch"}</button>
          <button type="button" onClick={toggleTheme}>{lang === "en" ? "Light / Dark" : "Hell / Dunkel"}</button>
        </div>
      </header>
      {children}
      {agency && (
        <footer className="footer tk-noprint tk-marke-footer">
          <div className="stack">
            <strong>{agency.name}</strong>
            <small>{[agency.address, agency.email, agency.website].filter(Boolean).join(" · ")}</small>
            <small><a href={`${WEBSITE_URL}/datenschutz.html`} target="_blank" rel="noopener noreferrer">{lang === "en" ? `Privacy (${PLATFORM_NAME})` : `Datenschutz (${PLATFORM_NAME})`}</a></small>
          </div>
        </footer>
      )}
      <Toasts />
    </>
  );
}

/** Text mit erkannten Links (http/https), Zeilenumbrüche bleiben. */
export function TextMitLinks({ text }: { text: string }) {
  return (
    <p className="tk-marke-text">
      {textParts(text).map((p, i) => (p.href
        ? <a key={i} href={p.href} target="_blank" rel="noopener noreferrer nofollow">{p.text}</a>
        : <span key={i}>{p.text}</span>))}
    </p>
  );
}

function Liste({ titel, eintraege }: { titel: string; eintraege: readonly string[] }) {
  if (!eintraege.length) return null;
  return (
    <div className="stack">
      <strong>{titel}</strong>
      <ul className="tk-marke-liste">{eintraege.map((e) => <li key={e}>{e}</li>)}</ul>
    </div>
  );
}

/** Briefing (strukturiert oder Freitext der Kampagne); in der Sprache der Seite beschriftet. */
export function BriefingAnsicht({ briefing, lang, titel = true, leer }: { briefing: EffectiveBriefing; lang: Lang; titel?: boolean; leer?: string }) {
  const L = BRIEFING_LABELS[lang];
  if (briefing.source === "none") return leer ? <p className="muted">{leer}</p> : null;
  if (briefing.source === "fallback") {
    return (
      <div className="stack">
        {titel && <strong>{L.fallback}</strong>}
        <TextMitLinks text={briefing.fallback_text} />
      </div>
    );
  }
  const tags = briefingCaptionTags(briefing);
  return (
    <div className="stack tk-marke-briefing">
      <Liste titel={L.keyMessages} eintraege={briefing.key_messages} />
      <div className="grid tk-marke-dodont">
        <Liste titel={L.dos} eintraege={briefing.dos} />
        <Liste titel={L.donts} eintraege={briefing.donts} />
      </div>
      {tags && (
        <div className="stack">
          <strong>{[briefing.hashtags.length ? L.hashtags : null, briefing.mentions.length ? L.mentions : null].filter(Boolean).join(" · ")}</strong>
          <p className="tk-mono tk-marke-tags">{tags}</p>
        </div>
      )}
      {(briefing.ad_label || briefing.paid_partnership) && (
        <div className="stack">
          <strong>{L.adLabel}</strong>
          {briefing.ad_label && <p>„{briefing.ad_label}“</p>}
          {briefing.paid_partnership && <p>{L.paid}</p>}
        </div>
      )}
      {briefing.deadline && <p><strong>{L.deadline}:</strong> {fmtDate(briefing.deadline, lang)}</p>}
      {briefing.notes && (
        <div className="stack">
          <strong>{L.notes}</strong>
          <TextMitLinks text={briefing.notes} />
        </div>
      )}
    </div>
  );
}
